import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Gift, BookOpen, Download, Users, CheckCircle2, Loader2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/useSeo";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  ["1", "Share your personal link", "Copy your unique referral link and send it to friends, colleagues or your followers."],
  ["2", "Your friend joins", "They sign up through your link and choose any paid plan — even the £4.99 intro month counts."],
  ["3", "Pick your free ebook", "You instantly earn a reward credit. Choose any ebook from the library and download it on the spot."],
];

export default function ReferFriendPage() {
  const { user } = useAuth();
  const [ebooks, setEbooks] = useState([]);
  const [me, setMe] = useState(null);
  const [claiming, setClaiming] = useState(null);

  useSeo({
    title: "Refer a Friend — Earn Free Ebooks",
    description: "Share your personal XpressPrompts link. Every friend who joins with a paid plan earns you a free downloadable ebook of your choice.",
  });

  const loadMe = () => api.get("/referrals/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/referrals/ebooks").then((r) => setEbooks(r.data)).catch(() => {});
    if (user) loadMe();
  }, [user]);

  const copyLink = () => {
    navigator.clipboard.writeText(me.share_url);
    toast.success("Referral link copied — share it anywhere!");
  };

  const claim = async (ebook) => {
    setClaiming(ebook.id);
    try {
      await api.post("/referrals/redeem", { ebook_id: ebook.id });
      toast.success(`"${ebook.title}" is yours — download it below`);
      loadMe();
    } catch (e) { toast.error(apiError(e)); }
    setClaiming(null);
  };

  return (
    <div className="bg-[#FAFAF7] min-h-screen" data-testid="refer-friend-page">
      {/* HERO */}
      <section className="bg-[#0B0F0D] text-white py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/assets/refer-a-friend.webp" alt="Refer a Friend" className="w-full max-w-xl mx-auto mb-8" data-testid="refer-hero-badge" />
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Share the shortcut. <span className="text-[#10B981]">Earn free ebooks.</span>
          </h1>
          <p className="text-gray-400 mt-4 text-sm sm:text-base max-w-2xl mx-auto">
            Every friend who joins through your personal link with any paid plan earns you a
            <strong className="text-[#D4AF37]"> free downloadable ebook of your choice</strong> from our reward library. No limits — every friend counts.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* PERSONAL LINK / CTA */}
        {user ? (
          me && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="refer-link-card">
              <h2 className="font-display font-extrabold text-xl flex items-center gap-2"><Gift size={20} className="text-emerald-700" /> Your personal referral link</h2>
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <input readOnly value={me.share_url} data-testid="refer-link-input"
                  className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono text-slate-700" />
                <button onClick={copyLink} data-testid="refer-copy-btn"
                  className="shrink-0 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold px-6 py-3 rounded-xl hover:brightness-105 active:scale-[0.98] transition-all">
                  <Copy size={15} /> COPY LINK
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-6">
                {[["Friends joined", me.friends_joined, "refer-stat-joined"], ["Friends gone paid", me.friends_paid, "refer-stat-paid"], ["Ebook credits", me.credits, "refer-stat-credits"]].map(([l, v, tid]) => (
                  <div key={l} className="bg-slate-50 rounded-xl p-4 text-center" data-testid={tid}>
                    <p className="font-display text-2xl font-extrabold text-emerald-700">{v}</p>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-1">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm" data-testid="refer-login-cta">
            <Users size={36} className="mx-auto text-emerald-700" />
            <h2 className="font-display font-extrabold text-xl mt-3">Log in to get your personal link</h2>
            <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">Your unique referral link is waiting in your account — share it and start earning free ebooks today.</p>
            <div className="flex justify-center gap-3 mt-5">
              <Link to="/login" data-testid="refer-login-btn" className="bg-slate-900 text-white font-bold px-6 py-3 rounded-xl hover:bg-emerald-700 transition-colors">Log in</Link>
              <Link to="/register" data-testid="refer-register-btn" className="bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold px-6 py-3 rounded-xl hover:brightness-105 transition-all">Create account</Link>
            </div>
          </div>
        )}

        {/* HOW IT WORKS */}
        <div>
          <h2 className="font-display font-extrabold text-2xl text-center">How it works</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            {STEPS.map(([n, t, d]) => (
              <div key={n} className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="h-9 w-9 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold flex items-center justify-center text-sm">{n}</div>
                <h3 className="font-display font-bold mt-4">{t}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CLAIMED EBOOKS */}
        {user && me && me.redemptions.length > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6" data-testid="refer-claimed-section">
            <h2 className="font-display font-extrabold text-lg flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-700" /> Your claimed ebooks</h2>
            <div className="mt-3 space-y-2">
              {me.redemptions.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 bg-white rounded-xl px-4 py-3" data-testid={`refer-claimed-${r.id}`}>
                  <p className="font-bold text-sm truncate">{r.ebook_title}</p>
                  <a href={`${API}/api/referrals/download/${r.id}`} data-testid={`refer-download-${r.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors">
                    <Download size={13} /> Download PDF
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EBOOK LIBRARY */}
        <div>
          <h2 className="font-display font-extrabold text-2xl text-center flex items-center justify-center gap-2"><BookOpen size={22} className="text-emerald-700" /> The reward library</h2>
          <p className="text-slate-500 text-sm text-center mt-2">Each friend who joins with a paid plan = 1 credit. Spend a credit on any ebook below.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-6" data-testid="refer-ebook-grid">
            {ebooks.map((e) => (
              <div key={e.id} className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col hover:border-emerald-300 hover:shadow-sm transition-all" data-testid={`refer-ebook-${e.id}`}>
                <h3 className="font-display font-bold text-slate-900">{e.title}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed flex-1">{e.description}</p>
                {user && me && (
                  <button onClick={() => claim(e)} disabled={me.credits < 1 || claiming === e.id} data-testid={`refer-claim-${e.id}`}
                    className="mt-4 inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {claiming === e.id ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
                    {me.credits >= 1 ? "Claim with 1 credit" : "Refer a friend to unlock"}
                  </button>
                )}
              </div>
            ))}
            {ebooks.length === 0 && <p className="text-sm text-slate-400 py-8 col-span-full text-center">The reward library is being stocked — check back soon.</p>}
          </div>
        </div>

        <p className="text-xs text-slate-400 text-center">Rewards are earned when a referred friend completes any paid purchase (the £4.99 intro month counts). Ebooks are digital downloads for personal use, from XpressPrompts™ · Voliciti Group Ventures Ltd.</p>
      </div>
    </div>
  );
}
